import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import PersonAvatarIcon from "@atlaskit/icon/core/person-avatar";
import {token} from "@atlaskit/tokens";
import {createFileRoute, notFound} from "@tanstack/react-router";
import {useMemo, useRef, useState} from "react";
import {twJoin} from "tailwind-merge";
import {AnankeTempDisabledInlineEditableTextField} from "~/components/anankeTemp/anankeTempDisabledInlineEditableTextField";
import {AnankeTempPageTitle} from "~/components/anankeTemp/anankeTempPageTitle";
import {AnankeTempVerticalSpacer} from "~/components/anankeTemp/anankeTempVerticalSpacer";
import {Breadcrumbs} from "~/components/breadcrumbs";
import {getEventContainerAddEventItems, EventContainerAddEventButton} from "~/components/eventContainerAddEventButton";
import {JourneyContactButton, JourneyMoreActionsButton} from "~/components/journeyProfileActionButtons";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {isDhiraaguWorkspace} from "~/domainModels/workspaceSpecific/dhiraagu";
import {AnankeField} from "@vesta/ananke/components/anankeField";
import {AnankeLoadingOverlay} from "~/components/anankeLoadingOverlay";
import {AnankeTab} from "@vesta/ananke/components/anankeTab";
import {AnankeTabList} from "@vesta/ananke/components/anankeTabList";
import {AnankeTabPanel} from "@vesta/ananke/components/anankeTabPanel";
import {AnankeTabs} from "@vesta/ananke/components/anankeTabs";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {getEventContainersWithEvents} from "~/utilities/apis/eventContainersApi";
import type {OttopilotEventContainerWithEvents as EventContainerWithEvents} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {formatDateTime, getUserDisplayName} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";
import {ArchiveEventContainerModal, DeleteEventContainerModal} from "./-archiveModals";
import {EventsTab} from "./-eventsTab";
import {LogsTab} from "./-logsTab";
import {DetailsTab, type DetailsTabHandle} from "./-detailsTab";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {isEventContainerEngagementRestrictedByGlobalConstraints} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import {resolveEventContainerEditPanelGlobalConstraints} from "~/utilities/eventContainerGlobalConstraints";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {
    eventContainerProfileSearchSchema,
    resolveEventContainerProfileBreadcrumbs,
} from "~/utilities/eventContainerProfileNavigation";
import {
    getEventContainerDispositionOptions,
    getOptionalEventContainerDispositionId,
} from "~/utilities/ottopilotSemanticColumns";

function getLatestEventsForEventContainer({eventContainer}: {eventContainer: EventContainerWithEvents}): EventContainerWithEvents {
    // Group events by templateId and keep only the latest one (by createdAt) for each
    const latestEventByTemplateId = new Map<string, EventContainerWithEvents["events"][number]>();

    for (const event of eventContainer.events) {
        const existingEvent = latestEventByTemplateId.get(event.templateId);
        if (existingEvent === undefined) {
            latestEventByTemplateId.set(event.templateId, event);
        } else {
            // Compare createdAt to keep the latest one
            if (new Date(event.createdAt) > new Date(existingEvent.createdAt)) {
                latestEventByTemplateId.set(event.templateId, event);
            }
        }
    }

    return {
        ...eventContainer,
        events: Array.from(latestEventByTemplateId.values()),
    };
}

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/event-containers/$eventContainerId/",
)({
    validateSearch: (search: Record<string, unknown>) => eventContainerProfileSearchSchema.parse(search),
    loaderDeps: ({search}) => ({eventContainerTemplateId: search.eventContainerTemplateId}),
    loader: async ({context, params, deps}) => {
        // await delay(2500);

        const eventContainerIdResult = uuidZodValidator.safeParse(params.eventContainerId);
        if (eventContainerIdResult.success !== true) {
            throw notFound();
        }
        const eventContainerId = eventContainerIdResult.data;
        if (deps.eventContainerTemplateId === undefined) {
            throw notFound();
        }

        const eventContainersResult = await getEventContainersWithEvents({
            accessToken: context.authenticationStatus.accessToken,
            id: eventContainerId,
            templateId: deps.eventContainerTemplateId,
            limit: 2,
            offset: 0,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: context.workspace.id,
        });
        if (eventContainersResult.success !== true) {
            throw eventContainersResult.error;
        }
        const eventContainers = eventContainersResult.data;
        if (eventContainers.length === 0) {
            throw notFound();
        }
        if (eventContainers.length > 1) {
            throw new Error("Constraint violated: Multiple Event Containers found for the same ID");
        }
        const eventContainer = eventContainers[0]!;
        return {
            eventContainer: getLatestEventsForEventContainer({eventContainer}),
            allEventContainerEvents: eventContainer.events,
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    const {eventContainer, allEventContainerEvents} = Route.useLoaderData();
    const search = Route.useSearch();
    const [tabNumber, setTabNumber] = useState(0);
    const detailsTabRef = useRef<DetailsTabHandle>(null);
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(eventContainer.templateId);
    const eventContainerConfiguration = eventContainerTemplate.configuration;
    const journeyMutationGlobalConstraints = useMemo(
        () =>
            resolveEventContainerEditPanelGlobalConstraints({
                workspace: workspace,
                workspaceCapabilities,
                editPanel: eventContainerConfiguration.eventContainersPage.editPanel,
            }),
        [workspace, workspaceCapabilities, eventContainerConfiguration.eventContainersPage.editPanel],
    );
    const areJourneyMutationsRestricted =
        isEventContainerEngagementRestrictedByGlobalConstraints(eventContainer, journeyMutationGlobalConstraints) === true;
    const breadcrumbItems = useMemo(
        () =>
            resolveEventContainerProfileBreadcrumbs({
                from: search.from,
                workspaceId: workspace.id,
                eventContainerTemplate,
                eventTemplates,
                isAmaronWorkspace: isAmaronWorkspace(workspaceCapabilities) === true,
            }),
        [eventContainerTemplate, eventTemplates, search.from, workspace],
    );

    // const journeyColumns: Array<ColDef<EventContainerWithEvents>> = useMemo(
    //     () => [
    //         // {
    //         //     field: "id",
    //         // },
    //         // {
    //         //     field: "createdBy",
    //         // },
    //         // {
    //         //     field: "createdAt",
    //         //     valueFormatter: (params) => formatDateTime(params.value),
    //         // },
    //         // {
    //         //     field: "updatedBy",
    //         // },
    //         // {
    //         //     field: "updatedAt",
    //         //     valueFormatter: (params) => formatDateTime(params.value),
    //         // },
    //         // {
    //         //     field: "status",
    //         //     valueFormatter: (params) => journeyStatusToDisplayName(params.value),
    //         // },
    //         ...journeysModuleConfiguration.additionalColumns.map((column) => ({
    //             headerName: column.displayName,
    //             // field: column.internalName,
    //             valueGetter: (row) => {
    //                 if (row.data === undefined) {
    //                     return "";
    //                 }
    //                 const journey: EventContainerWithEvents = row.data;

    //                 return journey.additionalColumnValues[column.id];
    //             },
    //         } satisfies OttopilotColumn<EventContainerWithEvents>)),
    //     ],
    //     [journeysModuleConfiguration],
    // );

    const [isDeleteModalOpened, setIsDeleteModalOpened] = useState<boolean>(false);
    const [isArchiveModalOpened, setIsArchiveModalOpened] = useState<boolean>(false);
    // const [isAuditLogsModalOpened, setIsAuditLogsModalOpened] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const callButtonColumnId = eventContainerConfiguration.eventContainersPage.callButtonColumnId;
    const whatsAppButtonColumnId = eventContainerConfiguration.eventContainersPage.whatsAppButtonColumnId;
    const journeyCallNumber =
        callButtonColumnId !== null
            ? ((eventContainer.additionalColumnValues[callButtonColumnId] as string | null | undefined) ?? null)
            : null;
    const journeyWhatsAppNumberCleaned = (() => {
        if (whatsAppButtonColumnId === null) {
            return null;
        }
        const whatsAppNumber = eventContainer.additionalColumnValues[whatsAppButtonColumnId] as string | null | undefined;
        if (whatsAppNumber === null || whatsAppNumber === undefined) {
            return null;
        }
        const cleaned = whatsAppNumber.replace(/[^0-9]/g, "");
        return cleaned === "" ? null : cleaned;
    })();
    // const customerQueryKeyForAuditLogs = uniqueUuid`cf7d1024-dc3c-4537-9673-9dd3ca053c56`;
    // const userQueryKeyForAuditLogs = uniqueUuid`df5e52ce-f9c6-44d8-9c9c-f7f4f858abbb`;

    return (
        <div className="w-full h-full p-ds-space-200 sm:p-ds-space-300 relative">
            {/* TODO: Remove this */}
            <AnankeLoadingOverlay
                visible={loading}
                message="Loading..."
            />

            <Breadcrumbs items={breadcrumbItems} />

            <div className="flex w-full justify-between gap-2 items-start">
                <AnankeTempPageTitle
                    className="truncate min-w-0"
                    title={processOttopilotStringTemplate(eventContainerConfiguration.displayNameTemplate, eventContainer.additionalColumnValues)}
                />

                <div className="flex shrink-0 w-max items-center gap-2">
                    <JourneyContactButton
                        callNumber={journeyCallNumber}
                        whatsAppNumberCleaned={journeyWhatsAppNumberCleaned}
                    />
                    {areJourneyMutationsRestricted !== true ? (
                        <EventContainerAddEventButton
                            items={getEventContainerAddEventItems({
                                workspace,
                                workspaceCapabilities,
                                eventContainerId: eventContainer.id,
                            })}
                        />
                    ) : null}

                    <JourneyMoreActionsButton
                        showArchive={isDhiraaguWorkspace(workspace) === true || isAmaronWorkspace(workspaceCapabilities) === true}
                        onArchive={() => {
                            setIsArchiveModalOpened(true);
                        }}
                        onDelete={() => {
                            setIsDeleteModalOpened(true);
                        }}
                    />
                </div>
            </div>

            <DeleteEventContainerModal
                isOpen={isDeleteModalOpened}
                setIsOpen={setIsDeleteModalOpened}
                loading={loading}
                setLoading={setLoading}
                eventContainer={eventContainer}
            />

            <ArchiveEventContainerModal
                isOpen={isArchiveModalOpened}
                setIsOpen={setIsArchiveModalOpened}
                loading={loading}
                setLoading={setLoading}
                eventContainer={eventContainer}
            />

            {/* <EventContainerAuditLogsModal
                isOpen={isAuditLogsModalOpened}
                setIsOpen={setIsAuditLogsModalOpened}
                eventContainerId={eventContainer.id}
            /> */}

            <AnankeTempVerticalSpacer
                above="pageTitle"
                below="formRow"
            />

            <AnankeTabs
                onChange={(value) => {
                    if (tabNumber === 2 && value !== 2) {
                        detailsTabRef.current?.requestLeave(() => setTabNumber(value));
                        return;
                    }
                    setTabNumber(value);
                }}
                selected={tabNumber}
                id={uniqueUuid`f577ffd9-65a9-45d2-bf8d-6755fe782067`}
            >
                <AnankeTabList>
                    <AnankeTab>Events</AnankeTab>
                    <AnankeTab>Logs</AnankeTab>
                    <AnankeTab>Details</AnankeTab>
                </AnankeTabList>

                <AnankeTabPanel>
                    <EventsTab
                        journey={eventContainer}
                        className="w-full pt-ds-space-150"
                        // style={{
                        //     paddingTop: token("space.200"),
                        // }}
                    />
                </AnankeTabPanel>

                <AnankeTabPanel>
                    <LogsTab
                        journey={eventContainer}
                        allJourneyEvents={allEventContainerEvents}
                        className="w-full pt-ds-space-150"
                        // style={{
                        //     paddingTop: token("space.200"),
                        // }}
                    />
                </AnankeTabPanel>

                <AnankeTabPanel>
                    <DetailsTab
                        ref={detailsTabRef}
                        journey={eventContainer}
                        loading={loading}
                        setLoading={setLoading}
                        className="w-full pt-ds-space-150"
                        // style={{
                        //     paddingTop: token("space.200"),
                        // }}
                    />
                </AnankeTabPanel>
            </AnankeTabs>

            {/* <VerticalSpacer className="h-ds-space-200 sm:h-ds-space-300" />

            <div className="w-full grid grid-cols-[auto] justify-end">
                <Button
                    appearance="primary"
                    onClick={() => createCustomerMutation.mutate()}
                    isLoading={createCustomerMutation.status === "idle" ? false : true}
                >
                    Add
                </Button>
            </div> */}
        </div>
    );
}

export function EventContainerSummaryCard({eventContainer}: {eventContainer: EventContainerWithEvents}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const eventContainerTemplate = useRequiredEventContainerTemplate(eventContainer.templateId);
    const {userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const dispositionId = getOptionalEventContainerDispositionId(eventContainer, eventContainerTemplate);
    const dispositionOptions = getEventContainerDispositionOptions(getAdditionalColumns, eventContainerTemplate, userDefinedObjectOptionsByTemplateId);

    const cardPadding = "1rem";

    return (
        <div
            className={twJoin(
                "w-full",
                // Border
                "border-ds-border border-ds-border-width border-solid",
                "rounded-[0.25rem]",
                "relative group",
            )}
            style={{
                padding: cardPadding,
                // borderRadius: token("radius.xlarge"),
            }}
        >
            <div className="grid grid-cols-1 content-start gap-ds-space-100">
                <AnankeField
                    id="id"
                    label="ID"
                >
                    <AnankeTempDisabledInlineEditableTextField value={eventContainer.id} />
                </AnankeField>

                <AnankeField
                    id="createdAt"
                    label="Created at"
                >
                    <AnankeTempDisabledInlineEditableTextField value={formatDateTime(eventContainer.createdAt)} />
                </AnankeField>

                <AnankeField
                    id="createdBy"
                    label="Created by"
                >
                    <div>
                        <UserRenderer userId={eventContainer.createdBy} />
                    </div>
                </AnankeField>

                <AnankeField
                    id="updatedAt"
                    label="Last updated at"
                >
                    <AnankeTempDisabledInlineEditableTextField value={formatDateTime(eventContainer.updatedAt)} />
                </AnankeField>

                <AnankeField
                    id="updatedBy"
                    label="Last updated by"
                >
                    <div>
                        <UserRenderer userId={eventContainer.updatedBy} />
                    </div>
                </AnankeField>

                <AnankeField
                    id="disposition"
                    label="Disposition"
                >
                    <AnankeTempDisabledInlineEditableTextField
                        value={
                            dispositionOptions.find((disposition) => disposition.id === dispositionId)?.displayName ?? "?"
                        }
                    />
                </AnankeField>
            </div>
        </div>
    );
}

// function JourneyAuditLogsModal({
//     isOpen,
//     setIsOpen,
//     eventContainerId,
// }: {
//     isOpen: boolean;
//     setIsOpen: (isOpen: boolean) => void;
//     eventContainerId: Uuid;
// }) {
//     return (
//         <Modal
//             opened={isOpen}
//             onClose={() => setIsOpen(false)}
//             title={`${journeysModuleConfiguration.moduleDisplayNameVariants.singularSentenceCase} logs`}
//             fullScreen={true}
//             classNames={{
//                 inner: "!p-12",
//                 // !bg-sg-surface-00
//                 // !bg-sg-ts-delayed-background-primary
//                 content:
//                     "!w-full !h-full !max-w-[1280px] gj-text-body-300 !rounded-xl p-6 !grid !grid-cols-1 !grid-rows-[auto_minmax(0,1fr)]",
//                 // header: "!bg-transparent !p-0",
//                 header: "!p-0 border-b border-solid border-[#000000]",
//                 body: "!p-0 w-full h-full grid grid-cols-1 grid-rows-1",
//             }}
//         >
//             <JourneyAuditLogs eventContainerId={eventContainerId} />
//         </Modal>
//     );
// }

export function UserRenderer({userId}: {userId: Uuid}) {
    const users = useUsers();
    const userTemplates = useUserTemplates();

    const displayName = getUserDisplayName(userId, users, userTemplates);
    if (displayName === "-") {
        return <AnankeTempDisabledInlineEditableTextField value="-" />;
    }

    return (
        <div
            className="w-full"
            style={{
                border: `${token("border.width.selected")} solid transparent`,
            }}
        >
            <div className="w-full px-ds-space-075 py-ds-space-100 grid grid-cols-[auto_minmax(0,1fr)] gap-x-ds-space-050 items-center">
                <PersonAvatarIcon label="" />
                <span className="line-clamp-1 break-all">{displayName}</span>
                {/* <span>{userTemplate.displayName}</span> */}
            </div>
        </div>
    );
}
